/*
 * pack-nudge.js  —  Bergamot Australia
 * ------------------------------------------------------------------
 * Nudges customers away from buying single-bottle variants in bulk
 * (which blows out shipping) and toward the 3-pack / 6-pack variants.
 *
 * Surfaces:  Product page (PDP)  +  Cart page  +  Cart drawer.
 * Behaviour: shows a warning with a live-calculated saving and a
 *            one-tap button that switches to / collapses into the pack.
 *
 * Built against FuelThemes "North" v7. No dependencies. No app.
 * All config lives in snippets/pack-nudge.liquid (#pack-nudge-config).
 * ------------------------------------------------------------------
 */
(function () {
  'use strict';

  var cfgEl = document.getElementById('pack-nudge-config');
  if (!cfgEl) return;

  var CFG;
  try { CFG = JSON.parse(cfgEl.textContent); } catch (e) { return; }

  var ROOT   = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var HANDLES = CFG.handles || [];
  var COPY    = CFG.copy || {};
  var MIN_QTY = CFG.minQtyToNudge || 3;      // start nudging at this single-qty
  var SYMBOL  = CFG.currencySymbol || '$';

  var modelCache = {};   // handle -> pack model
  var swapping   = false; // reentrancy guard for cart writes
  var renderLock = false; // coalesce cart re-renders

  // ---- helpers -----------------------------------------------------

  function money(cents) { return SYMBOL + (cents / 100).toFixed(2); }

  function tmpl(str, vars) {
    return String(str || '').replace(/\{(\w+)\}/g, function (_, k) {
      return (vars && k in vars) ? vars[k] : '';
    });
  }

  // "3 x Original Bottle (1L)" -> 3.  Combo packs ("... and ...") excluded.
  function packSize(title) {
    if (!title) return null;
    if (/\band\b|\+/i.test(title)) return null;
    var m = title.match(/^\s*(\d+)\s*[x\u00d7]/i);
    return m ? parseInt(m[1], 10) : null;
  }

  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { if (!r.ok) throw new Error(url); return r.json(); });
  }

  // Build (and cache) the pack model for a product: sizes -> {id,price,optionValue}
  function getModel(handle) {
    if (modelCache[handle]) return Promise.resolve(modelCache[handle]);
    return getJSON(ROOT + 'products/' + handle + '.js').then(function (p) {
      var packs = {};
      p.variants.forEach(function (v) {
        var n = packSize(v.title || v.public_title || v.name);
        if (n && !packs[n]) {
          packs[n] = { id: v.id, price: v.price, optionValue: v.option1, title: v.title };
        }
      });
      var model = { handle: handle, title: p.title, packs: packs, single: packs[1] || null };
      modelCache[handle] = model;
      return model;
    });
  }

  // Which pack to recommend for a given single-unit quantity (+ display vars)
  function recommend(model, qty) {
    if (!model.single || qty < MIN_QTY) return null;
    var p1 = model.single.price;

    if (qty >= 6 && model.packs[6]) {
      var p6 = model.packs[6].price;
      return {
        band: 6,
        pack: model.packs[6],
        copyKey: 'six',
        vars: {
          pack:      '6-pack',
          save:      money(6 * p1 - p6),
          perBottle: money(p1 - Math.round(p6 / 6)),
          totalSix:  money(6 * p1 - p6)
        }
      };
    }
    if (qty >= 3 && model.packs[3]) {
      var p3 = model.packs[3].price;
      return {
        band: 3,
        pack: model.packs[3],
        copyKey: 'three',
        vars: { pack: '3-pack', save: money(3 * p1 - p3) }
      };
    }
    return null;
  }

  // Optimal collapse of N singles -> {6:x, 3:y, 1:z}
  function decompose(qty, model) {
    var out = { 6: 0, 3: 0, 1: 0 }, rem = qty;
    if (model.packs[6]) { out[6] = Math.floor(rem / 6); rem = rem % 6; }
    if (model.packs[3] && rem >= 3) { out[3] = 1; rem -= 3; }
    out[1] = rem;
    return out;
  }

  // ---- shared UI ---------------------------------------------------

  function buildNudge(model, rec, kind, onClick) {
    var wrap = document.createElement('div');
    wrap.className = 'pack-nudge pack-nudge--' + kind;

    var msg = document.createElement('p');
    msg.className = 'pack-nudge__text';
    msg.textContent = tmpl(COPY[rec.copyKey] || '', rec.vars);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'pack-nudge__btn';
    btn.textContent = tmpl(COPY.button || 'Switch to {pack}', rec.vars);
    btn.addEventListener('click', function (e) {
      e.preventDefault();
      btn.disabled = true;
      Promise.resolve(onClick()).catch(function () {}).then(function () { btn.disabled = false; });
    });

    wrap.appendChild(msg);
    wrap.appendChild(btn);
    return wrap;
  }

  // ---- cart re-render (mirrors the theme's own Section Rendering) ---

  function applyCartUpdate(updates) {
    var onCartPage = !!document.getElementById('Cart');
    var body = { updates: updates, sections: ['cart-drawer'], sections_url: location.pathname };

    return fetch(ROOT + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })
    .then(function (r) { if (!r.ok) throw new Error('update failed'); return r.json(); })
    .then(function (state) {
      // Drawer (present on every page) — swap inner + re-init the theme's controller
      var drawer = document.getElementById('Cart-Drawer');
      var html = state.sections && state.sections['cart-drawer'];
      if (drawer && html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.cart-drawer');
        if (parsed) {
          drawer.innerHTML = parsed.innerHTML;
          if (typeof CartDrawer !== 'undefined') { try { new CartDrawer(); } catch (e) {} }
        }
      }
      // Cart count bubble(s)
      if (typeof state.item_count === 'number') {
        document.querySelectorAll('.float_count').forEach(function (el) {
          el.textContent = state.item_count;
        });
      }
      // Cart page table re-renders itself via the theme's listener
      if (onCartPage) document.dispatchEvent(new CustomEvent('cart:refresh'));

      setTimeout(scheduleCartRender, 80);
    });
  }

  // Collapse this product's single-bottle line into packs
  function swapInCart(model) {
    if (swapping) return Promise.resolve();
    swapping = true;
    return getJSON(ROOT + 'cart.js').then(function (cart) {
      var have = {};
      cart.items.forEach(function (it) { have[it.id] = (have[it.id] || 0) + it.quantity; });

      var singleId = model.single && model.single.id;
      var singleQty = singleId ? (have[singleId] || 0) : 0;
      if (singleQty < MIN_QTY) return;

      var target = decompose(singleQty, model);
      var updates = {};
      if (singleId) updates[singleId] = target[1] || 0;      // absolute (0 removes the line)
      [6, 3].forEach(function (sz) {
        if (model.packs[sz] && target[sz]) {
          var id = model.packs[sz].id;
          updates[id] = (have[id] || 0) + target[sz];        // add to any existing pack line
        }
      });
      return applyCartUpdate(updates);
    }).then(function () { swapping = false; }, function () { swapping = false; });
  }

  // ---- cart surfaces (page + drawer) -------------------------------

  function clearCartNudges() {
    document.querySelectorAll('.pack-nudge--cart, .pack-nudge--drawer').forEach(function (n) { n.remove(); });
  }

  function renderCartNudges() {
    clearCartNudges();
    return getJSON(ROOT + 'cart.js').then(function (cart) {
      var jobs = [];
      cart.items.forEach(function (it) {
        if (HANDLES.indexOf(it.handle) === -1) return;
        if (packSize(it.variant_title) !== 1) return;         // single-bottle lines only
        jobs.push(getModel(it.handle).then(function (model) {
          var rec = recommend(model, it.quantity);
          if (rec) injectCartBanners(model, rec);
        }).catch(function () {}));
      });
      return Promise.all(jobs);
    }).catch(function () {});
  }

  function injectCartBanners(model, rec) {
    var onClick = function () { return swapInCart(model); };

    // Cart page — above the items table
    var form = document.querySelector('.thb-cart-form, #cart-form');
    if (form && !form.querySelector('.pack-nudge--cart[data-h="' + model.handle + '"]')) {
      var b1 = buildNudge(model, rec, 'cart', onClick);
      b1.setAttribute('data-h', model.handle);
      var table = form.querySelector('.main-cart-items');
      if (table) table.parentNode.insertBefore(b1, table);
      else form.insertBefore(b1, form.firstChild);
    }

    // Cart drawer — top of the mini-cart
    var content = document.querySelector('#Cart-Drawer .widget_shopping_cart_content');
    if (content && !content.querySelector('.pack-nudge--drawer[data-h="' + model.handle + '"]')) {
      var b2 = buildNudge(model, rec, 'drawer', onClick);
      b2.setAttribute('data-h', model.handle);
      var list = content.querySelector('.mini-cart');
      if (list) content.insertBefore(b2, list);
      else content.insertBefore(b2, content.firstChild);
    }
  }

  function scheduleCartRender() {
    if (renderLock) return;
    renderLock = true;
    setTimeout(function () { renderLock = false; renderCartNudges(); }, 50);
  }

  // ---- product page (PDP) ------------------------------------------

  function pdpHandle() {
    var m = location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function initPDP() {
    var handle = pdpHandle();
    if (!handle || HANDLES.indexOf(handle) === -1) return;

    var form = document.querySelector('product-form form, form[data-type="add-to-cart-form"]');
    if (!form) return;
    var scope = form.closest('.shopify-section') || document;

    getModel(handle).then(function (model) {
      if (!model.single) return;

      var container = form.closest('product-form') || form;
      var qtyInput  = container.querySelector('.qty, input[name="quantity"]');
      var idInput   = form.querySelector('input[name="id"]');

      var box = document.createElement('div');
      box.className = 'pack-nudge pack-nudge--pdp';
      box.hidden = true;
      var anchor = container.querySelector('.add_to_cart_holder') || form;
      anchor.insertAdjacentElement('afterend', box);

      function currentVariantId() {
        return idInput && idInput.value ? parseInt(idInput.value, 10) : null;
      }
      function isSingleSelected() {
        var vid = currentVariantId();
        return vid && model.single && vid === model.single.id;
      }
      function pdpSwitch(rec) {
        var val = rec.pack.optionValue;
        var done = false;
        // radios / selects live outside <product-form>, associated by form= attr
        scope.querySelectorAll('input[type="radio"]').forEach(function (r) {
          if (r.value === val) { r.checked = true; r.dispatchEvent(new Event('change', { bubbles: true })); done = true; }
        });
        if (!done) scope.querySelectorAll('select').forEach(function (s) {
          if ([].some.call(s.options, function (o) { return o.value === val; })) {
            s.value = val; s.dispatchEvent(new Event('change', { bubbles: true })); done = true;
          }
        });
        if (qtyInput) {
          var q = 1;
          if (rec.band === 6) q = Math.max(1, Math.floor((parseInt(qtyInput.value, 10) || 6) / 6));
          qtyInput.value = q;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (box) box.hidden = true;
      }
      function evaluate() {
        var qty = parseInt(qtyInput && qtyInput.value, 10) || 1;
        if (!isSingleSelected()) { box.hidden = true; return; }
        var rec = recommend(model, qty);
        if (!rec) { box.hidden = true; return; }
        var fresh = buildNudge(model, rec, 'pdp', function () { pdpSwitch(rec); });
        box.innerHTML = '';
        while (fresh.firstChild) box.appendChild(fresh.firstChild);
        box.hidden = false;
      }

      container.addEventListener('input', evaluate);
      container.addEventListener('change', evaluate);
      document.addEventListener('product:variant-change', function () { setTimeout(evaluate, 0); });
      if (idInput) new MutationObserver(evaluate).observe(idInput, { attributes: true, attributeFilter: ['value'] });
      evaluate();
    }).catch(function () {});
  }

  // ---- boot --------------------------------------------------------

  function init() {
    initPDP();
    scheduleCartRender();
    ['cart:item-added', 'cart-drawer:open', 'line-item:change:end', 'cart:refresh']
      .forEach(function (ev) { document.addEventListener(ev, scheduleCartRender); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();